import {
  chain,
  filter,
  find,
  findIndex,
  get,
  gt,
  includes,
  isEmpty,
  isEqual,
  map,
  some,
  sortBy,
  toLower,
} from 'lodash';

import { Observable, rx } from '@app/shared/rx/rx-wrapper';
import { IBaseEntityType } from '@app/shared/types/entities/base/base.type';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';
import { SiteType } from '@app/shared/types/enums/site-type.enum';
import { ITreeData } from '@app/shared/types/common/tree.type';
import { INTERNAL_GRID_ROW_ID } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component.constants';
import { generateId } from '@app/shared/utils/common-utils';
import { RowNode } from 'ag-grid-community';
import { isLeaf } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { formatUserDisplayName } from '@app/shared/utils/user-utils';
import { I18n } from '@app/i18n/strings-keys';

/**
 * Check if a user has at least one role that exists in the permitted roles list.
 * The permittedRoleIds are pre-filtered role IDs that have the required hawker session permissions.
 */
export const hasHawkerSessionPermission = (
  user: IBaseEntityType,
  permittedRoleIds: string[],
): boolean => {
  const userRoleIds: string[] = map(get(user, 'roles', []), 'role_id');

  return some(userRoleIds, (roleId) => includes(permittedRoleIds, roleId));
};

export const convertEventsToTree = (
  events: IBaseEntityType[],
  users: IBaseEntityType[],
  roles: IBaseEntityType[],
  existingItems: IBaseEntityType[] = [],
): ITreeData[] => {
  if (!events || !events.length) {
    return [];
  }

  const userList: IBaseEntityType[] = users || [];
  const permittedRoleIds: string[] = map(roles || [], 'id');

  const eventNodes: ITreeData[] = map(events, (event) => {
    const eventId: string = get(event, 'id', '');
    const eventName: string = get(event, 'name', '');
    const eventDate: string = get(
      event,
      ['event_days', 0, 'start_date_time'],
      '',
    );
    const availableSites: IBaseEntityType[] = filter(
      get(event, 'available_sites', []),
      { business_type: SiteType.VendingCommissary },
    );

    const existingEvent: IBaseEntityType = find(existingItems, {
      event_id: eventId,
    });
    const existingAssignments: IBaseEntityType[] = get(
      existingEvent,
      'commissary_assignments',
      [],
    );

    let someChildChanged = false;

    const siteChildren: ITreeData[] = map(availableSites, (site) => {
      const siteId: string = get(site, 'id', '');

      const existingAssignment: IBaseEntityType = find(
        existingAssignments,
        (assignment) => isEqual(get(assignment, 'site_id', ''), siteId),
      );
      const userId: string = get(existingAssignment, 'user_id');

      const managerOptions = sortBy(
        map(
          filter(userList, (user) => {
            const userSites: IBaseEntityType[] = get(user, 'sites', []);
            const isAssignedToSite = some(userSites, (userSite) =>
              isEqual(get(userSite, 'id', ''), siteId),
            );

            return (
              isAssignedToSite &&
              hasHawkerSessionPermission(user, permittedRoleIds)
            );
          }),
          (user) => {
            const managerId = get(user, 'id', '');

            return {
              text: formatUserDisplayName(user),
              value: managerId,
              selected: !isEmpty(userId) && isEqual(userId, managerId),
            };
          },
        ),
        (item) => toLower(item.text),
      );

      // Check if this site's assigned manager is no longer available
      // This covers 3 cases:
      // 1. Manager (User) is deleted from Portal
      // 2. The vending commissary site is removed from the manager
      // 3. The manager's permission/role is changed
      const managerNoLongerAvailable =
        !isEmpty(userId) &&
        !some(managerOptions, (option) => option.value === userId);

      // Track if any child site has manager availability issues
      // This flag will be propagated to the parent event node
      if (managerNoLongerAvailable && !someChildChanged) {
        someChildChanged = true;
      }

      // If manager is no longer available AND there are no other options,
      // add a placeholder option so the dropdown doesn't get auto-disabled
      // This allows user to see and potentially clear the selection
      let finalManagerOptions = managerOptions;
      if (managerNoLongerAvailable && isEmpty(managerOptions)) {
        finalManagerOptions = [
          {
            text: I18n.Forms.GridForm.NotAssigned,
            value: '',
            selected: false,
          },
        ];
      }

      return {
        isLeaf: true,
        site_id: siteId,
        site_name: get(site, 'name', ''),
        store_number: get(site, 'store_number', ''),
        user_id: gt(findIndex(finalManagerOptions, { value: userId }), -1)
          ? userId
          : '',
        managerOptions: finalManagerOptions,
        // hasWarning: flag indicating manager is unavailable (for row highlighting and parent propagation)
        hasWarning: managerNoLongerAvailable,
        [INTERNAL_GRID_ROW_ID]: `group_${generateId()}`,
      };
    });

    const sortedSiteChildren: ITreeData[] = sortBy(siteChildren, (item) =>
      toLower(get(item, 'site_name', '')),
    );

    // someChildChanged flag marks event as "changed" when manager issues are detected
    // This ensures: 1) yellow highlighting on parent/child rows (hasWarning), 2) auto-activate save button (setControlWhenInit)
    return {
      hasWarning: someChildChanged,
      event_id: get(event, 'id', ''),
      event_name: eventName,
      event_date: eventDate,
      event_status: get(event, 'status', ''),
      children: sortedSiteChildren,
      [INTERNAL_GRID_ROW_ID]: `group_${generateId()}`,
      isLeaf: false,
    };
  });

  return eventNodes;
};

export const eventsFilterValueGetter = (
  data: IBaseEntityType,
  { node }: { node: RowNode },
): string[] => {
  // use parent level data for filtering, so that the filter value is the same for all child nodes of the same event
  const eventData = isLeaf(node) ? get(node, 'parent.data', {}) : data;

  return [get(eventData, 'event_id', '')];
};

export const getEventsObs = (options: {
  dependentData$: Observable<Record<string, any>>;
}) => {
  return options.dependentData$.pipe(
    rx.map((dependentData) => {
      return chain(get(dependentData, [ENTITY_TYPES.EVENTS, 'items'], []))
        .sortBy((event) => toLower(get(event, 'name', '')))
        .map((event) => ({
          ...event,
          text: event.name,
          value: event.id,
        }))
        .value();
    }),
  );
};
